/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * Chkk Analytics API v1.0
 * Chkk Analytics API for Kubernetes deployment and upgrade safety.
 * OpenAPI spec version: v1.0
 */
import useSwr from 'swr';
import type { Key, SWRConfiguration } from 'swr';

import useSWRMutation from 'swr/mutation';
import type { SWRMutationConfiguration } from 'swr/mutation';

import type {
  CreateQueryExecutionRequest,
  CreateQueryRequest,
  ListQueriesParams,
  ListQueriesResponse,
  ListQueryExecutionsParams,
  ListQueryExecutionsResponse,
  ListTablesParams,
  ListTablesResponse,
  Query,
  QueryExecution,
  Table,
  UpdateQueryRequest,
} from './analytics-models';

import { customAnalyticsApiInstance } from './custom-instance';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * List tables
 */
export const listTables = (params?: ListTablesParams) => {
  return customAnalyticsApiInstance<ListTablesResponse>({
    url: `/tables`,
    method: 'GET',
    params,
  });
};

export const getListTablesKey = (params?: ListTablesParams) =>
  [`/tables`, ...(params ? [params] : [])] as const;

export type ListTablesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTables>>
>;
export type ListTablesQueryError = unknown;

export const useListTables = <TError = unknown>(
  params?: ListTablesParams,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listTables>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getListTablesKey(params) : null));
  const swrFn = () => listTables(params);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};

/**
 * Get details of a specific table
 */
export const getTable = (tableName: string) => {
  return customAnalyticsApiInstance<Table>({
    url: `/tables/${tableName}`,
    method: 'GET',
  });
};

export const getGetTableKey = (tableName: string) =>
  [`/tables/${tableName}`] as const;

export type GetTableQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTable>>
>;
export type GetTableQueryError = unknown;

export const useGetTable = <TError = unknown>(
  tableName: string,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof getTable>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false && !!tableName;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getGetTableKey(tableName) : null));
  const swrFn = () => getTable(tableName);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};

/**
 * List queries
 */
export const listQueries = (params?: ListQueriesParams) => {
  return customAnalyticsApiInstance<ListQueriesResponse>({
    url: `/queries`,
    method: 'GET',
    params,
  });
};

export const getListQueriesKey = (params?: ListQueriesParams) =>
  [`/queries`, ...(params ? [params] : [])] as const;

export type ListQueriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listQueries>>
>;
export type ListQueriesQueryError = unknown;

export const useListQueries = <TError = unknown>(
  params?: ListQueriesParams,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listQueries>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListQueriesKey(params) : null));
  const swrFn = () => listQueries(params);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};

/**
 * Create a query
 */
export const createQuery = (createQueryRequest: CreateQueryRequest) => {
  return customAnalyticsApiInstance<void>({
    url: `/queries`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createQueryRequest,
  });
};

export const getCreateQueryMutationFetcher = () => {
  return (_: Key, { arg }: { arg: CreateQueryRequest }) => {
    return createQuery(arg);
  };
};
export const getCreateQueryMutationKey = () => [`/queries`] as const;

export type CreateQueryMutationResult = NonNullable<
  Awaited<ReturnType<typeof createQuery>>
>;
export type CreateQueryMutationError = unknown;

export const useCreateQuery = <TError = unknown>(options?: {
  swr?: SWRMutationConfiguration<
    Awaited<ReturnType<typeof createQuery>>,
    TError,
    Key,
    CreateQueryRequest,
    Awaited<ReturnType<typeof createQuery>>
  > & { swrKey?: string };
}) => {
  const { swr: swrOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getCreateQueryMutationKey();
  const swrFn = getCreateQueryMutationFetcher();

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};

/**
 * Get details of a specific query
 */
export const getQuery = (queryId: string) => {
  return customAnalyticsApiInstance<Query>({
    url: `/queries/${queryId}`,
    method: 'GET',
  });
};

export const getGetQueryKey = (queryId: string) =>
  [`/queries/${queryId}`] as const;

export type GetQueryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getQuery>>
>;
export type GetQueryQueryError = unknown;

export const useGetQuery = <TError = unknown>(
  queryId: string,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof getQuery>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false && !!queryId;
  const swrKey =
    swrOptions?.swrKey ?? (() => (isEnabled ? getGetQueryKey(queryId) : null));
  const swrFn = () => getQuery(queryId);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};

/**
 * Update a query
 */
export const updateQuery = (
  queryId: string,
  updateQueryRequest: UpdateQueryRequest,
) => {
  return customAnalyticsApiInstance<void>({
    url: `/queries/${queryId}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updateQueryRequest,
  });
};

export const getUpdateQueryMutationFetcher = (queryId: string) => {
  return (_: Key, { arg }: { arg: UpdateQueryRequest }) => {
    return updateQuery(queryId, arg);
  };
};
export const getUpdateQueryMutationKey = (queryId: string) =>
  [`/queries/${queryId}`] as const;

export type UpdateQueryMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateQuery>>
>;
export type UpdateQueryMutationError = unknown;

export const useUpdateQuery = <TError = unknown>(
  queryId: string,
  options?: {
    swr?: SWRMutationConfiguration<
      Awaited<ReturnType<typeof updateQuery>>,
      TError,
      Key,
      UpdateQueryRequest,
      Awaited<ReturnType<typeof updateQuery>>
    > & { swrKey?: string };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const swrKey = swrOptions?.swrKey ?? getUpdateQueryMutationKey(queryId);
  const swrFn = getUpdateQueryMutationFetcher(queryId);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};

/**
 * List query executions
 */
export const listQueryExecutions = (
  queryId: string,
  params?: ListQueryExecutionsParams,
) => {
  return customAnalyticsApiInstance<ListQueryExecutionsResponse>({
    url: `/queries/${queryId}/executions`,
    method: 'GET',
    params,
  });
};

export const getListQueryExecutionsKey = (
  queryId: string,
  params?: ListQueryExecutionsParams,
) => [`/queries/${queryId}/executions`, ...(params ? [params] : [])] as const;

export type ListQueryExecutionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listQueryExecutions>>
>;
export type ListQueryExecutionsQueryError = unknown;

export const useListQueryExecutions = <TError = unknown>(
  queryId: string,
  params?: ListQueryExecutionsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listQueryExecutions>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false && !!queryId;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListQueryExecutionsKey(queryId, params) : null));
  const swrFn = () => listQueryExecutions(queryId, params);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};

/**
 * Create a query execution
 */
export const createQueryExecution = (
  queryId: string,
  createQueryExecutionRequest: CreateQueryExecutionRequest,
) => {
  return customAnalyticsApiInstance<QueryExecution>({
    url: `/queries/${queryId}/executions`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createQueryExecutionRequest,
  });
};

export const getCreateQueryExecutionMutationFetcher = (queryId: string) => {
  return (_: Key, { arg }: { arg: CreateQueryExecutionRequest }) => {
    return createQueryExecution(queryId, arg);
  };
};
export const getCreateQueryExecutionMutationKey = (queryId: string) =>
  [`/queries/${queryId}/executions`] as const;

export type CreateQueryExecutionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createQueryExecution>>
>;
export type CreateQueryExecutionMutationError = unknown;

export const useCreateQueryExecution = <TError = unknown>(
  queryId: string,
  options?: {
    swr?: SWRMutationConfiguration<
      Awaited<ReturnType<typeof createQueryExecution>>,
      TError,
      Key,
      CreateQueryExecutionRequest,
      Awaited<ReturnType<typeof createQueryExecution>>
    > & { swrKey?: string };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const swrKey =
    swrOptions?.swrKey ?? getCreateQueryExecutionMutationKey(queryId);
  const swrFn = getCreateQueryExecutionMutationFetcher(queryId);

  const query = useSWRMutation(swrKey, swrFn, swrOptions);

  return {
    swrKey,
    ...query,
  };
};

/**
 * Get details of a specific query execution
 */
export const getQueryExecution = (queryId: string, executionId: string) => {
  return customAnalyticsApiInstance<QueryExecution>({
    url: `/queries/${queryId}/executions/${executionId}`,
    method: 'GET',
  });
};

export const getGetQueryExecutionKey = (queryId: string, executionId: string) =>
  [`/queries/${queryId}/executions/${executionId}`] as const;

export type GetQueryExecutionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getQueryExecution>>
>;
export type GetQueryExecutionQueryError = unknown;

export const useGetQueryExecution = <TError = unknown>(
  queryId: string,
  executionId: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getQueryExecution>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false && !!(queryId && executionId);
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getGetQueryExecutionKey(queryId, executionId) : null));
  const swrFn = () => getQueryExecution(queryId, executionId);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions,
  );

  return {
    swrKey,
    ...query,
  };
};
